import type { BoardDto, BoardPostDetail, CreateBoardPostInput, TargetInput } from '../../../api/boards';
import type { TargetRow } from '../../BoardsPage/TargetPicker';
// 본문이 HTML 이 되며 필요해진 둘 — 옛 글 열기(글자→HTML) · 「사실상 비었나」 판정.
// tiptap 을 안 부르는 순수 함수라 여기서 정적으로 불러도 번들 비용이 없다.
import { htmlIsBlank, textToEditorHtml } from '../../../components/editor/editor-html';

/**
 * 글쓰기 · 수정 폼의 값 — 시안 v1 `axi-board-write` (U4 · 2026-09-15).
 *
 * ★ 보내는 값은 현행 `BoardWriter` 와 **한 글자도 다르지 않다**(같은 서버 · 같은 규칙).
 *   댓글 허용이 게시판 기본값과 같으면 null 로 보내 서버 기본을 따르게 하는 것까지 같다.
 * ★ 수정에는 알림을 싣지 않는다(규칙 13) — 다시 알림은 따로 누른다.
 * ★ 열람자는 게시판 「게시글 별 열람자 설정」이 켜졌을 때만 싣는다(규칙 2). 서버도 꺼진 게시판의 readers 는 무시한다.
 */

/**
 * 서버 `boards-helpers.ts` 의 `POST_TITLE_MAX` 와 **같아야 한다.**
 * `components/editor/content-length.server-contract.test.ts` 가 서버 상수를 읽어 맞춰 본다 —
 * 값이 지금 같더라도 그 시험은 «비어 있지 않다». **서버가 바꾸는 날 빨개진다.**
 * (본문 상한에서 실제로 그렇게 됐다 — 바로 아래 참조.)
 */
export const TITLE_MAX = 300;    // 서버 POST_TITLE_MAX — 시험이 묶는다
/**
 * 서버 `boards-helpers.ts` 의 `POST_CONTENT_MAX` 와 **같아야 한다.**
 *
 * ## ★ 여기서 실제로 일어난 일 (2026-09-23) — 「주석」과 「시험」의 차이
 *
 * 서버가 10,000 → 50,000 으로 올랐다(리치 에디터를 대비한 것 — 표 하나가 글자보다 25배 넘게
 * 먹는다). **이 줄에는 「서버 `POST_CONTENT_MAX`」라는 주석이 이미 있었는데 따라오지 않았다.**
 * 그리고 이 값은 `<textarea maxLength>` 로 쓰이므로(`BoardWriteScreen.tsx`) 어긋난 채 나가면
 * 사용자가 **서버가 받아 줄 글을 못 쓰게** 된다.
 *
 * ⚠ **어긋난 자리는 `main` 이다 — 운영이 아니었다.** 운영 배포본에서는 서버도 10,000 이어서
 *   **둘이 맞아 있었다**(결함이 없었다). 정확히 말하면 **「상한을 올린 효과는 서버·프론트가
 *   «함께» 나가야 사용자에게 닿는다」**이고, 이 고침이 **서버만 먼저 나가는 갈래를 막았다.**
 *   ⛔ 이것을 「운영이 깨져 있었다」로 적으면 다음 사람이 잘못 읽는다.
 *
 * ⇒ 그래서 주석을 믿지 않고 **시험으로 묶었다** —
 *   `components/editor/content-length.server-contract.test.ts` 가 서버 상수를 읽어 이 값과
 *   맞춰 본다. 서버가 또 바꾸면 그 시험이 빨개져 알려 준다.
 */
export const CONTENT_MAX = 50_000; // 서버 POST_CONTENT_MAX (boards-helpers.ts) — 시험이 묶는다

export interface WriteAttachment {
  fileId: string;
  name: string;
  sizeBytes: number;
  mimeType?: string;
}

export interface WriteForm {
  title: string;
  content: string;
  prefixId: string;
  isAnonymous: boolean;
  isSecret: boolean;
  allowComments: boolean;
  readers: TargetRow[];
  /** 목록 위에 고정 — 운영자만. 등록 · 저장 뒤 pin 호출로 반영한다(서버가 등록과 한 번에 받지 않는다). */
  pinned: boolean;
  /** 새 글만 — 수정에서는 쓰지 않는다. */
  notify: boolean;
  notifyTargets: TargetRow[];
}

/**
 * 폼의 `content` 는 **HTML** 이다 (2026-09-23 리치 에디터).
 *
 * ⛔ 옛 글(`contentType !== 'HTML'`)은 **글자**로 저장돼 있으므로 그대로 열면 tiptap 이
 *   HTML 로 파스해 **줄바꿈이 사라진다.** 그래서 열 때 한 번 HTML 로 바꾼다
 *   (`textToEditorHtml` — 꺽쇠는 글자로 escape 하고 개행은 `<br>`·빈 줄은 문단으로).
 * ★ 고쳐 저장하면 그 글은 `contentType: 'HTML'` 이 된다 — **한 방향 올림**이고, 그것이
 *   맞다(에디터로 고친 글은 HTML 이다). 되돌릴 일은 없다.
 */
export function initialForm(board: BoardDto, post?: BoardPostDetail | null): WriteForm {
  return {
    title: post?.title ?? '',
    content: post == null
      ? ''
      : (post.contentType === 'HTML' ? (post.content ?? '') : textToEditorHtml(post.content ?? '')),
    prefixId: post?.prefix?.id ?? '',
    isAnonymous: post?.isAnonymous ?? false,
    isSecret: post?.isSecret ?? false,
    allowComments: post ? post.commentsAllowed : board.allowComments,
    readers: (post?.readers ?? []).map((r) => ({ targetType: r.targetType, targetId: r.targetId })),
    pinned: post?.isPinned ?? false,
    notify: !post && board.notifyEnabled && board.notifyAllByDefault,
    notifyTargets: [],
  };
}

const toTargetInput = (r: TargetRow): TargetInput => ({ targetType: r.targetType, targetId: r.targetId ?? '0' });

/** 새 글 · 수정이 같이 쓰는 값 — 두 벌로 쓰면 한쪽에만 항목이 붙는다. */
export function buildCommonPayload(form: WriteForm, board: BoardDto, attachments: readonly WriteAttachment[]): CreateBoardPostInput {
  return {
    title: form.title.trim(),
    content: form.content.trim(),
    /**
     * ★ 에디터가 HTML 을 낸다 (2026-09-23).
     * ⛔ 이 칸은 **수정에서도 «필수»**다 — 서버가 본문을 바꿀 때 없으면 400 을 낸다
     *   (`boards.routes.ts` 의 `CONTENT_TYPE_REQUIRED`). 새 글·수정이 이 한 벌을 같이 쓰므로
     *   여기 한 줄이 둘을 다 덮는다. `write-model.test.ts` 가 그것을 잠근다.
     */
    contentType: 'HTML',
    prefixId: form.prefixId || null,
    isAnonymous: form.isAnonymous,
    isSecret: form.isSecret,
    allowComments: form.allowComments === board.allowComments ? null : form.allowComments,
    attachments: attachments.map((a) => ({
      fileId: a.fileId,
      name: a.name,
      sizeBytes: a.sizeBytes,
      ...(a.mimeType ? { mimeType: a.mimeType } : {}),
    })),
    readers: board.usePostReaders ? form.readers.map(toTargetInput) : undefined,
  };
}

export function buildCreatePayload(form: WriteForm, board: BoardDto, attachments: readonly WriteAttachment[]): CreateBoardPostInput {
  return {
    ...buildCommonPayload(form, board, attachments),
    notify: board.notifyEnabled
      ? { enabled: form.notify, targets: form.notifyTargets.map((r) => ({ ...toTargetInput(r), canNotify: true })) }
      : undefined,
  };
}

/** 수정 — 알림은 싣지 않는다(규칙 13). */
export function buildPatchPayload(form: WriteForm, board: BoardDto, attachments: readonly WriteAttachment[]): Partial<CreateBoardPostInput> {
  return buildCommonPayload(form, board, attachments);
}

const targetKey = (r: TargetRow) => `${r.targetType}:${r.targetId ?? ''}`;
export function sameTargets(a: readonly TargetRow[], b: readonly TargetRow[]): boolean {
  if (a.length !== b.length) return false;
  const x = a.map(targetKey).sort();
  const y = b.map(targetKey).sort();
  return x.every((k, i) => k === y[i]);
}

export type ChangedField =
  | 'prefix' | 'title' | 'content' | 'attachments' | 'anonymous' | 'secret' | 'comments' | 'pinned' | 'readers' | 'notify';

/** 「바뀐 항목 n」 · 버리기 창 판정 — 처음 값과 지금 값을 항목별로 견준다. */
export function changedFields(
  before: WriteForm,
  after: WriteForm,
  beforeFiles: readonly WriteAttachment[],
  afterFiles: readonly WriteAttachment[],
): ChangedField[] {
  const out: ChangedField[] = [];
  if (before.prefixId !== after.prefixId) out.push('prefix');
  if (before.title !== after.title) out.push('title');
  if (before.content !== after.content) out.push('content');
  const ids = (l: readonly WriteAttachment[]) => l.map((f) => f.fileId).join(',');
  if (ids(beforeFiles) !== ids(afterFiles)) out.push('attachments');
  if (before.isAnonymous !== after.isAnonymous) out.push('anonymous');
  if (before.isSecret !== after.isSecret) out.push('secret');
  if (before.allowComments !== after.allowComments) out.push('comments');
  if (before.pinned !== after.pinned) out.push('pinned');
  if (!sameTargets(before.readers, after.readers)) out.push('readers');
  if (before.notify !== after.notify || !sameTargets(before.notifyTargets, after.notifyTargets)) out.push('notify');
  return out;
}

/**
 * ⛔ 본문이 HTML 이 되면서 **글자 길이로는 「비었나」를 알 수 없다** — 빈 에디터는 `''` 가
 *   아니라 `<p></p>` 를 내므로 `content.trim().length > 0` 이 «참»이 되어 **빈 글이 저장된다.**
 *   ⇒ `htmlIsBlank` 로 본다(그림·표만 있는 글은 글자가 0 이어도 «빈 것이 아니다»).
 */
export const canSubmitForm = (form: WriteForm) => form.title.trim().length > 0 && !htmlIsBlank(form.content);
